import { api } from '../configs/apiConfig';

class TasksService {
    // Get all tasks for the current user
    async getMyTasks(userId) {
        try {
            const response = await api.get(`/tasks/user/${userId}`);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Get details of a specific task
    async getTaskDetails(taskId, userId) {
        try {
            const response = await api.get(`/tasks/user/${userId}/task/${taskId}`);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Get predicted next/pending approvers for current task step
    async getPredictedNextApprovers(taskId, userId) {
        try {
            const response = await api.get(`/tasks/user/${userId}/task/${taskId}/predicted-next-approvers`);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Perform an action on a task (Approve, Reject, Cancel)
    async performTaskAction(taskId, action, comments = '', userId) {
        try {
            const response = await api.post(`/tasks/user/${userId}/task/${taskId}/action`, {
                action,
                comments
            });
            return response.data;
        } catch (error) {
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error('Response error:', error.response.data);
                if (error.response.status === 404) {
                    throw new Error('Task not found or you do not have permission to perform this action');
                }
                throw new Error(error.response.data.title || 'An error occurred while performing the action');
            } else if (error.request) {
                // The request was made but no response was received
                console.error('Request error:', error.request);
                throw new Error('No response received from server');
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error('Error:', error.message);
                throw new Error('Error setting up request');
            }
        }
    }

    // Error Handler
    handleError(error) {
        if (error.response) {
            console.error('Response error:', error.response.data);
            throw new Error(error.response.data.message || 'An error occurred');
        } else if (error.request) {
            console.error('Request error:', error.request);
            throw new Error('No response received from server');
        } else {
            console.error('Error:', error.message);
            throw new Error('Error setting up request');
        }
    }

    // Check if user can edit their task action
    async canEditTaskAction(taskId, userId) {
        try {
            const response = await api.get(`/tasks/user/${userId}/task/${taskId}/can-edit`);
            return response.data;
        } catch (error) {
            this.handleError(error);
            return false;
        }
    }

    // Edit (rollback) user's last action on a task
    async editTaskAction(taskId, userId) {
        try {
            const response = await api.post(`/tasks/user/${userId}/task/${taskId}/edit`);
            return response.data;
        } catch (error) {
            if (error.response) {
                console.error('Response error:', error.response.data);
                if (error.response.status === 404) {
                    throw new Error('Task not found or you do not have permission to edit this action');
                }
                if (error.response.status === 400) {
                    throw new Error(error.response.data.title || 'Cannot edit this task action');
                }
                throw new Error(error.response.data.title || 'An error occurred while editing the action');
            } else if (error.request) {
                console.error('Request error:', error.request);
                throw new Error('No response received from server');
            } else {
                console.error('Error:', error.message);
                throw new Error('Error setting up request');
            }
        }
    }

    // ============ ADMIN METHODS ============

    // Get all tasks (admin view) with QoS metrics
    // If userId is provided, filters by user's auditor assignments
    async getAllTasks(userId = null) {
        try {
            const url = userId ? `/admin/tasks/all?userId=${userId}` : `/admin/tasks/all`;
            const response = await api.get(url);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Get tasks filtered by status
    async getTasksByStatus(status) {
        try {
            const response = await api.get(`/admin/tasks/by-status/${status}`);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Get tasks filtered by compliance status
    async getTasksByCompliance(compliance) {
        try {
            const response = await api.get(`/admin/tasks/by-compliance/${compliance}`);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Get aging tasks
    async getAgingTasks() {
        try {
            const response = await api.get(`/admin/tasks/aging`);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Get task statistics
    async getTaskStatistics() {
        try {
            const response = await api.get(`/admin/tasks/statistics`);
            return response.data;
        } catch (error) {
            this.handleError(error);
        }
    }

    // Assign task to a unit for dynamic assignment steps
    async assignTaskToUnit(taskId, unitId, approvalType, userIds = [], comments = '', userId, action = 'Approve') {
        try {
            const response = await api.post(`/tasks/user/${userId}/task/${taskId}/assign-to-unit`, {
                organizationalUnitId: unitId,
                approvalType: approvalType,
                userIds: userIds,
                comments: comments,
                action: action
            });
            return response.data;
        } catch (error) {
            if (error.response?.data?.title) {
                throw new Error(error.response.data.title);
            }
            throw error;
        }
    }

    // Check if a ProcessType has dynamic assignment in first workflow step for a specific user
    async checkProcessTypeHasDynamicAssignment(processTypeName, userId) {
        try {
            console.log(`[TasksService Debug] Making API call to check dynamic assignment for ${processTypeName} and user ${userId}`);
            const response = await api.get(`/tasks/check-dynamic-assignment/${processTypeName}/${userId}`);
            console.log(`[TasksService Debug] API Response:`, response.data);
            return response.data;
        } catch (error) {
            console.error('[TasksService Error] Error checking dynamic assignment:', error);
            console.error('[TasksService Error] Status:', error.response?.status);
            console.error('[TasksService Error] Data:', error.response?.data);
            return false;
        }
    }
}

export default new TasksService(); 